/**
 * tutorSpaceBridge — le pont d'une route poussée de l'espace tuteur, posé dans `navigationScreens.js`
 * une fois pour toutes par le câblage de la coquille. Le pendant de `studentSpaceBridge`.
 *
 * Il rend l'écran que le lot a inscrit pour cette route (`tutorSpaceRoutes`) quand l'interrupteur
 * `tutor_ds` est relu allumé pour ce compte, que ce compte est un tuteur ou un candidat (pas un
 * élève : `selectTutorDsReading`) — un candidat n'ouvre que les routes de sa candidature
 * (`tutorCandidateRoutes`), un tuteur que les autres —, et — seulement pour une entrée qui ne déclare pas vivre en live
 * (`livesInLive`) — hors live, et jamais passée par le live. Sinon, ou tant que le lot n'a rien inscrit, l'écran legacy, props
 * intactes.
 *
 * Tant que l'interrupteur n'est pas relu pour ce compte (juste après une connexion), une route que le
 * design system prendrait ne montre pas le legacy le temps de la relecture : un chargement neutre
 * (`TutorSpaceWaiting`), qui laisse la place à l'un ou l'autre écran — ou au legacy, passé un délai,
 * si la relecture ne vient pas.
 *
 * Sur une route partagée avec l'élève, il entoure `studentSpaceBridge(route, Legacy)` : les deux
 * lisent des rôles qui ne se recouvrent pas (le tuteur qui est aussi élève garde l'espace de l'élève),
 * et un seul composant de route choisit ainsi l'espace par le rôle, sinon le legacy.
 *
 * Comme les autres ponts : aucun crochet avant la branche — la condition arrive par `connect`, qui
 * redessine le pont quand elle change —, et l'écran des ports ne reçoit que `route`.
 */
import React, { useEffect, useState } from "react"
import { ActivityIndicator, View } from "react-native"
import { connect } from "react-redux"

import {
    isTutorCandidate,
    selectTutorDsReading,
    TUTOR_SPACE_WAIT_MS,
    type TutorDsReading,
} from "../../../../../featureFlags/tutorDsFF"
import { selectAccount } from "../../../view-models/shared/displayedStudent"
import {
    readRouteEntry,
    type TutorSpaceRouteEntry,
    type TutorSpaceRouteName,
    type TutorSpaceScreen,
} from "./tutorSpaceRouteTypes"

/** Le registre et l'hôte, chargés à la première route allumée seulement (voir les `require` plus bas). */
type TutorSpaceRoutesModule = typeof import("./tutorSpaceRoutes")
type TutorSpaceScreenHostModule = typeof import("./TutorSpaceScreenHost")

type BridgeState = { liveMode?: { isLiveModeEnabled?: boolean } } | undefined

/**
 * Ce que le pont lit du store : l'interrupteur relu pour ce compte de tuteur, le live, et si le compte
 * passe sa candidature (il ne lit alors que le registre de la candidature).
 */
export type TutorSpaceContext = { reading: TutorDsReading; inLive: boolean; candidate: boolean }

export function selectTutorSpaceContext(state: unknown): TutorSpaceContext {
    const root = state as BridgeState
    return {
        reading: selectTutorDsReading(state),
        inLive: !!root?.liveMode?.isLiveModeEnabled,
        candidate: isTutorCandidate(selectAccount(state as Parameters<typeof selectAccount>[0])?.roles),
    }
}

/** La route s'ouvrirait-elle au design system, l'interrupteur allumé ? Hors live, ou une entrée qui y vit. */
function opensWhenOn(entry: TutorSpaceRouteEntry, inLive: boolean): boolean {
    return !inLive || readRouteEntry(entry).inLive
}

/** L'écran que le pont rend pour une entrée du registre, ou `undefined` : le legacy. */
export function tutorSpaceScreenFor(
    entry: TutorSpaceRouteEntry | undefined,
    context: TutorSpaceContext
): TutorSpaceScreen | undefined {
    if (entry == null || context.reading !== "on") return undefined
    return opensWhenOn(entry, context.inLive) ? readRouteEntry(entry).screen : undefined
}

/**
 * Le pont attend-il la relecture ? Seulement quand elle est en cours pour ce compte ET que la route a
 * un écran inscrit qui s'ouvrirait allumé : c'est le seul cas où le legacy serait un éclair de l'ancien.
 */
export function tutorSpaceWaitsFor(entry: TutorSpaceRouteEntry | undefined, context: TutorSpaceContext): boolean {
    return entry != null && context.reading === "pending" && opensWhenOn(entry, context.inLive)
}

/**
 * Les routes du design system qui ne sont qu'une feuille (« Gérer une séance ») : elles se posent
 * PAR-DESSUS l'écran d'où vient le tuteur. La pile (`@react-navigation/stack`) pose chaque route sur une
 * carte opaque et détache l'écran d'en dessous : la feuille s'y ouvrait au centre d'un fond gris uni, et
 * le tuteur croyait qu'une nouvelle fenêtre s'était ouverte (Wajih, 03/11). En modale transparente,
 * l'écran d'origine reste monté et visible derrière le voile de la feuille, et y revenir ne relit rien.
 * Pas d'animation de la carte : c'est la feuille qui entre (`<Modal>`).
 */
const TUTOR_SPACE_SHEET_ROUTES: readonly TutorSpaceRouteName[] = ["move-session"]

export const PRESENTED_OVER_PREVIOUS_SCREEN = {
    presentation: "transparentModal",
    cardStyle: { backgroundColor: "transparent" },
    cardOverlayEnabled: false,
    animationEnabled: false,
} as const

/**
 * Les options de pile qu'une route pontée ajoute aux siennes : la présentation par-dessus pour une
 * feuille, quand c'est l'écran du design system qui la rend (interrupteur relu allumé, hors live). Lue par
 * la table des routes (`navigationScreens.js`) à chaque fois que la pile construit ses écrans : la carte
 * est transparente dès sa première peinture, et l'ancien écran, page pleine, garde sa carte opaque. Le
 * lien profond garde son écran d'en dessous : la pile démarre sur `menu` (`initialRouteName` d'`App.js`).
 */
export function tutorSpaceRouteOptions(
    name: TutorSpaceRouteName,
    context: TutorSpaceContext
): typeof PRESENTED_OVER_PREVIOUS_SCREEN | Record<string, never> {
    const sheet = TUTOR_SPACE_SHEET_ROUTES.includes(name) && context.reading === "on" && !context.inLive
    return sheet ? PRESENTED_OVER_PREVIOUS_SCREEN : {}
}

/** Ce que dit le lecteur d'écran de l'attente : la roue seule ne se lit pas. */
export const TUTOR_SPACE_WAIT_LABEL = "Chargement de ton espace"

/** Le chargement neutre de l'attente, puis le legacy passé `TUTOR_SPACE_WAIT_MS` : jamais d'attente sans fin. */
function TutorSpaceWaiting(props: { fallback: React.ReactNode }) {
    const [late, setLate] = useState(false)
    useEffect(() => {
        const timer = setTimeout(() => setLate(true), TUTOR_SPACE_WAIT_MS)
        return () => clearTimeout(timer)
    }, [])
    if (late) return <>{props.fallback}</>
    return (
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
            <ActivityIndicator size="large" accessibilityLabel={TUTOR_SPACE_WAIT_LABEL} />
        </View>
    )
}

type BridgeProps = {
    /** L'écran des ports à rendre, lu sur le store ; `undefined` : le legacy. */
    portsScreen: TutorSpaceScreen | undefined
    /** L'interrupteur n'est pas encore relu pour ce compte, et la route serait au design system. */
    waiting: boolean
    // Le sac de props legacy (navigation, route, et ce que la pile y ajoute) : relayé tel quel.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [legacyProp: string]: any
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function tutorSpaceBridge(routeName: TutorSpaceRouteName, LegacyScreen: React.ComponentType<any>) {
    const Bridge = (props: BridgeProps) => {
        const { portsScreen: Screen, waiting, ...legacyProps } = props
        if (waiting) return <TutorSpaceWaiting fallback={<LegacyScreen {...legacyProps} />} />
        if (!Screen) return <LegacyScreen {...legacyProps} />
        // `require` paresseux, comme le registre : éteint, rien de l'espace tuteur n'est évalué.
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { TutorSpaceScreenHost } = require("./TutorSpaceScreenHost") as TutorSpaceScreenHostModule
        return <TutorSpaceScreenHost Screen={Screen} route={legacyProps.route} />
    }
    Bridge.displayName = `TutorSpaceBridge(${routeName})`

    // Une route ouverte en live y reste : une entrée qui ne déclare pas `livesInLive` garde son écran
    // legacy quand le live se ferme sous elle, au lieu d'être remplacée en plein geste par l'écran des
    // ports. Les entrées qui vivent en live (la salle, la prescription et le bilan) s'ouvrent au design
    // system dans les deux cas. D'où la forme « fabrique » de `connect` : un `mapState` par instance du
    // pont, qui retient d'avoir vu le live.
    // `{}` en second argument : pas de `dispatch` glissé dans les props du legacy.
    //
    // La garde d'une route sur l'état est lue une fois, à la première lecture du registre, puis retenue :
    // un écran qui change d'arbre en pleine séance démonterait la salle sous le tuteur (son label
    // `events-panel`). La garde sur les paramètres, elle, est relue à chaque fois : ils disent qui ouvre
    // l'écran (la relecture anonyme des activités), et une route réutilisée avec d'autres paramètres
    // ne garde pas la réponse des premiers.
    return connect(() => {
        let seenInLive = false
        let stateGuarded: boolean | undefined
        return (state: unknown, ownProps: { route?: { params?: unknown } }) => {
            const read = selectTutorSpaceContext(state)
            seenInLive ||= read.inLive
            const context = { ...read, inLive: seenInLive }
            // Le registre n'est lu qu'allumé, ou le temps de la relecture pour un tuteur.
            const registry =
                context.reading !== "off"
                    ? // eslint-disable-next-line @typescript-eslint/no-require-imports
                      (require("./tutorSpaceRoutes") as TutorSpaceRoutesModule)
                    : undefined
            // Le candidat ne lit que sa candidature, le tuteur jamais elle (`tutorCandidateRoutes`) ; les
            // gardes sont celles des routes du tuteur.
            const table = context.candidate ? registry?.tutorCandidateRoutes : registry?.tutorSpaceRoutes
            const stateGuard = context.candidate ? undefined : registry?.tutorSpaceRouteGuards?.[routeName]
            if (registry != null && stateGuarded === undefined) {
                stateGuarded = stateGuard != null && !stateGuard(state)
            }
            const paramGuard = context.candidate ? undefined : registry?.tutorSpaceRouteParamGuards?.[routeName]
            const guarded = stateGuarded === true || (paramGuard != null && !paramGuard(ownProps.route?.params))
            const entry = guarded
                ? undefined
                : (table as Partial<Record<string, TutorSpaceRouteEntry>> | undefined)?.[routeName]
            return { portsScreen: tutorSpaceScreenFor(entry, context), waiting: tutorSpaceWaitsFor(entry, context) }
        }
    }, {})(Bridge)
}
